import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { operationType } from "../../../../../services/types";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { webURL } from "../../../../../services/API";
import axios from "axios";
import { AuthenticateSelect } from "../../../../auth/select";
import { TextInput } from "../../../../form/textInput";
import { updateNodeValue } from "../../../../../services/method";
import { Loader, Main, Styled } from "./style";
import { Select } from "../../../../form/select";
import { getGoogleAccountToken } from "../../../../auth/method";
import { File, Folder } from "lucide-react";

type GoogleDriveTriggerProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

export function GoogleDriveTrigger({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: GoogleDriveTriggerProps) {
  const { setNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({
    isToggle: false,
  });
  const [isExpanded, setIsExpanded] = React.useState(false);
  // const [isCopy, setIsCopy] = React.useState<boolean>(false);
  // const [webhookUrl, setWebhookUrl] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isWatching, setIsWatching] = React.useState<boolean>(false);
  const [watchStatus, setWatchStatus] = React.useState<string>("");
  const [watchType, setWatchType] = React.useState<"file" | "folder">("folder");
  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const [files, setFiles] = React.useState<any[]>([]);
  const [folders, setFolders] = React.useState<any[]>([]);
  const [googleToken, setGoogleToken] = React.useState<string>("");
  // const [lastPageToken, setLastPageToken] = React.useState<string>("");

  React.useEffect(() => {
    if (setChildMinimize)
      setChildMinimize(() => {
        setIsExpanded((prev) => !prev);
      });
    if (setChildSettings)
      setChildSettings(() => {
        setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChildMinimize, setChildSettings]);

  // Initialize webhook URL with projectId and nodeId in the URL
  React.useEffect(() => {
    // Check if already watching
    if (data.value?.watchChannel?.channelId) {
      const status = data.value.watchChannel.status || "enabled";

      // Check if expired
      if (
        status === "expired" ||
        (data.value.watchChannel.expiration &&
          new Date(data.value.watchChannel.expiration).getTime() < Date.now())
      ) {
        setIsWatching(false);
        setWatchStatus("⚠️ Watch expired - please re-enable");
      } else if (status === "enabled") {
        setIsWatching(true);
        setWatchStatus(
          `✅ Watching ${watchType} (Channel: ${data.value.watchChannel.channelId})`
        );
      } else if (status === "disabled") {
        setIsWatching(false);
        setWatchStatus("⚠️ Watch disabled");
      } else {
        setIsWatching(false);
        setWatchStatus(`⚠️ Watch status: ${status}`);
      }
    }

    // Detect watch type from stored data
    if (data.value?.fileId) {
      setWatchType("file");
    } else if (data.value?.folderId) {
      setWatchType("folder");
    }
  }, [
    data.value?.watchChannel,
    data.value?.fileId,
    data.value?.folderId,
    data.value?.pageToken,
    id,
    watchType,
  ]);

  // Handle authentication
  const handleAuthenticate = async (type: "add" | "remove", authData: any) => {
    const getGoogleToken = await getGoogleAccountToken(authData, id, setNodes);
    console.log("Retrieved Google token:", getGoogleToken);
    setGoogleToken(getGoogleToken);
    console.log("Google token set in state.", getGoogleToken);
    console.log("Authentication data:", authData);
    console.log("Authentication data:", getGoogleToken);
    if (type === "add") {
      // 1. Save connection
      updateNodeValue(id, setNodes, { connection: authData });
    } else if (type === "remove") {
      updateNodeValue(id, setNodes, {
        connection: null,
        watchChannel: null,
        pageToken: null,
        fileId: "",
        folderId: "",
      });
      setIsWatching(false);
      setWatchStatus("");
      setFiles([]);
      setFolders([]);
    }
  };

  // Handle input changes
  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    keyRef?: string
  ) => {
    const value = e.target.value;
    if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: value });
  };

  // Handle watch type toggle
  const handleWatchTypeChange = (type: "file" | "folder") => {
    setWatchType(type);
    // Clear the opposite field
    if (type === "file") {
      updateNodeValue(id, setNodes, { folderId: "" });
    } else {
      updateNodeValue(id, setNodes, { fileId: "" });
    }
  };

  // Fetch files and folders from Google Drive
  const handleFetchDriveItems = async () => {
    if (!data.value?.connection) return;
    setIsFetching(true);

    try {
      const projectId = new URLSearchParams(window.location.search).get("id");
      let result;
      if (watchType === "file") {
        // Use backend fetch if no token
        console.log("Using backend to fetch files from Google Drive...");
        console.log("Fetching files from Google Drive...", googleToken);
        result = await axios.post(
          `${webURL}/blocks/drive/fetch/files`,
          {
            projectId: projectId || undefined,
            nodeId: id,
            connection: data.value?.connection,
          },
          googleToken ? { headers: { Authorization: `Bearer ${googleToken}` }} : {}
        );
        if (result.data.success) {
          const fetchedFiles = result.data.files.map((file: any, index: number) => ({
            id: file.id,
            name: file.name,
            mimeType: file.mimeType,
            isActive: data.value?.fileId ? file.id === data.value?.fileId : index === 0 ,
          }));
          setFiles(fetchedFiles);
          if (!data.value?.fileId) updateNodeValue(id, setNodes, { fileId: fetchedFiles[0].id, folderId: "" });
          console.log(`✅ Fetched ${fetchedFiles.length} files`);
          return fetchedFiles;
        }
      } else {
        result = await axios.post(
          `${webURL}/blocks/drive/fetch/folders`,
          {
            projectId: projectId || undefined,
            nodeId: id,
            connection: data.value?.connection,
          },
          googleToken ? { headers: { Authorization: `Bearer ${googleToken}` }} : {}
        );
        if (result.data.success) {
          const fetchedFolders = result.data.folders.map((folder: any, index: number) => ({
            id: folder.id,
            name: folder.name,
            isActive: data.value?.folderId ? folder.id === data.value?.folderId : index === 0,
          }));
          setFolders(fetchedFolders);
          if (!data.value?.folderId) updateNodeValue(id, setNodes, { folderId: fetchedFolders[0].id, fileId: "" });
          console.log(`✅ Fetched ${fetchedFolders.length} folders`);
          return fetchedFolders;
        }
      }
    } catch (error: any) {
      console.error("Fetch drive items error:", error);
    } finally {
      setIsFetching(false);
    }
  };

  // Handle file/folder selection
  const handleSelectItem = (itemId: string) => {
    if (watchType === "file") {
      setFiles((prevFiles) =>
        prevFiles.map((file) => ({
          ...file,
          isActive: file.id === itemId,
        }))
      );
      updateNodeValue(id, setNodes, { fileId: itemId, folderId: "" });
    } else {
      setFolders((prevFolders) =>
        prevFolders.map((folder) => ({
          ...folder,
          isActive: folder.id === itemId,
        }))
      );
      updateNodeValue(id, setNodes, { folderId: itemId, fileId: "" });
    }
  };

  // Enable Google Drive Watch
  const handleEnableWatch = async () => {
    if (!data.value?.connection) {
      // alert("Please connect your Google account first");
      return;
    }

    const fileId = data.value?.fileId;
    const folderId = data.value?.folderId;

    if (!fileId && !folderId) {
      // alert("Please enter a File ID or Folder ID to watch");
      return;
    }

    setIsLoading(true);
    setWatchStatus("Enabling watch...");

    try {
      // Get projectId from URL
      const urlParams = new URLSearchParams(window.location.search);
      const projectId = urlParams.get("id");

      if (!projectId) {
        throw new Error("Project ID not found in URL");
      }

      // Call the enable watch endpoint
      const response = await axios.post(
        `${webURL}/triggers/drive/watch/enable`,
        {
          projectId,
          nodeId: id,
          fileId: fileId || undefined,
          folderId: folderId || undefined,
          connection: data.value?.connection,
        },
          googleToken ? { headers: { Authorization: `Bearer ${googleToken}` }} : {}
      );

      if (response.data.success) {
        const watchData = response.data;

        // Update node with watch channel info and page token
        updateNodeValue(id, setNodes, {
          watchChannel: {
            channelId: watchData.channelId,
            resourceId: watchData.resourceId,
            expiration: watchData.expiration,
            webhookUrl: watchData.webhookUrl,
            status: "enabled",
            createdAt: new Date().toISOString(),
          },
          pageToken: watchData.pageToken,
        });

        setIsWatching(true);
        setWatchStatus(
          `✅ Watching ${watchType} (Channel: ${watchData.channelId})`
        );
      } else {
        setWatchStatus("❌ Failed to enable watch");
      }
    } catch (error: any) {
      console.error("Enable watch error:", error);
      setWatchStatus(
        `❌ Error: ${error.response?.data?.message || error.message}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Disable Google Drive Watch
  const handleDisableWatch = async () => {
    if (!data.value?.watchChannel) {
      return;
    }

    setIsLoading(true);
    setWatchStatus("Disabling watch...");

    try {
      // Get projectId from URL
      const urlParams = new URLSearchParams(window.location.search);
      const projectId = urlParams.get("id");

      if (!projectId) {
        throw new Error("Project ID not found in URL");
      }

      // Call the disable watch endpoint
      const response = await axios.post(
        `${webURL}/triggers/drive/watch/disable`,
        {
          projectId,
          nodeId: id,
        },   googleToken ? { headers: { Authorization: `Bearer ${googleToken}` }} : {}
      );

      if (response.data.success) {
        // Clear watch channel info (keep pageToken for potential re-enable)
        updateNodeValue(id, setNodes, {
          watchChannel: {
            ...data.value.watchChannel,
            status: "disabled",
            disabledAt: new Date().toISOString(),
          },
        });

        setIsWatching(false);
        setWatchStatus("✅ Watch disabled successfully");
      } else {
        setWatchStatus("Failed to disable watch");
      }
    } catch (error: any) {
      console.error("Disable watch error:", error);
      setWatchStatus(
        `Error: ${error.response?.data?.message || error.message}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isWarning = !data.value?.connection;
  // if(isWarning && isExpanded) handleFetchDriveItems();
  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon">
              <IoWarningOutline size={21} />
            </span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon">
              <IoCheckmarkCircleOutline size={21} />
            </span>
          </>
        ) : null}
        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: "420px" }}>
            {isLoading ? (
              <Loader />
            ) : (
              <Styled>
                {/* Google Authentication */}
                <AuthenticateSelect
                  type="google"
                  defaultId={data.value?.connection?.id}
                  handleAuthenticate={handleAuthenticate}
                  background={data.background}
                />

                <div className="action">
                  {data.value?.connection && !isWatching ? (
                    <button
                      className="btn1 withBorder"
                      onClick={handleFetchDriveItems}
                      disabled={isFetching}>
                      {isFetching ? "Fetching..." :      watchType === "file" ? "Get File List" : "Get Folder List"}
                    </button>
                  ) : null}
                </div>

                {/* Watch Type Selection */}
                <div className="watch-type-section ">
                  {/* <h4>Watch Type</h4> */}
                  <div className="watch-type-buttons">
                    <button
                      className={`type-button ${
                        watchType === "file" ? "active" : ""
                      }`}
                      onClick={() => handleWatchTypeChange("file")}
                      disabled={isWatching}>
                      <File size={ 18 } /> File
                    </button>
                    <button
                      className={`type-button ${
                        watchType === "folder" ? "active" : ""
                      }`}
                      onClick={() => handleWatchTypeChange("folder")}
                      disabled={isWatching}>
                      <Folder size={ 18 } /> Folder
                    </button>
                  </div>
                </div>

                {/* File or Folder Selection */}
                <div
                  className={`selection-section ${
                    isFetching || isWatching ? "disabled" : ""
                  }`}>
                  {watchType === "file" && files.length > 0 ? (
                    <Select
                      title="File"
                      data={files}
                      handleSelectItem={handleSelectItem}
                      isSmall={true}
                    />
                  ) : watchType === "folder" && folders.length > 0 ? (
                    <Select 
                      title="Folder"
                      data={folders}
                      handleSelectItem={handleSelectItem}
                      isSmall={true}
                    />
                  ) : (
                    <>
                      {watchType === "file" ? (
                        <TextInput
                          keyRef="fileId"
                          value={data.value?.fileId || ""}
                          title="File ID"
                          placeholder="File ID"
                          handleChange={handleChangeInput}
                        />
                      ) : (
                        <TextInput
                          keyRef="folderId"
                          value={data.value?.folderId || ""}
                          title="Folder ID"
                          placeholder="Folder ID"
                          handleChange={handleChangeInput}
                        />
                      )}
                    </>
                  )}
                </div>

                {/* Enable/Disable Watch Section */}
                <div className="watch-section">
                  {!isWatching ? (
                    <button
                      className="watch-button enable btn1"
                      onClick={handleEnableWatch}
                      disabled={isLoading || !data.value?.connection}>
                      <FontAwesomeIcon icon={faSync} spin={isLoading} />
                      Enable Watch
                    </button>
                  ) : (
                    <button
                      className="watch-button disable btn1"
                      onClick={handleDisableWatch}
                      disabled={isLoading}>
                      <FontAwesomeIcon icon={faSync} spin={isLoading} />
                      Disable Watch
                    </button>
                  )}

                  {watchStatus && (
                    <div
                      className={`watch-status ${
                        isWatching
                          ? "success"
                          : watchStatus.includes("❌")
                          ? "error"
                          : "info"
                      }`}>
                      {watchStatus}
                    </div>
                  )}
                </div>

                {/* Watch Info Display */}
                {data.value?.watchChannel && (
                  <div className="watch-info">
                    <h5>Watch Details:</h5>
                    <ul>
                      <li>
                        <strong>Status:</strong>{" "}
                        <span
                          className={
                            data.value.watchChannel.status === "enabled"
                              ? "status-enabled"
                              : "status-disabled"
                          }>
                          {data.value.watchChannel.status || "enabled"}
                        </span>
                      </li>
                      <li>
                        <strong>Channel ID:</strong>{" "}
                        {data.value.watchChannel.channelId}
                      </li>
                      <li>
                        <strong>Resource ID:</strong>{" "}
                        {data.value.watchChannel.resourceId}
                      </li>
                      {data.value.watchChannel.expiration && (
                        <li>
                          <strong>Expires:</strong>{" "}
                          {new Date(
                            data.value.watchChannel.expiration
                          ).toLocaleString()}
                        </li>
                      )}
                      {data.value.watchChannel.createdAt && (
                        <li>
                          <strong>Created:</strong>{" "}
                          {new Date(
                            data.value.watchChannel.createdAt
                          ).toLocaleString()}
                        </li>
                      )}
                      {data.value.watchChannel.disabledAt && (
                        <li>
                          <strong>Disabled:</strong>{" "}
                          {new Date(
                            data.value.watchChannel.disabledAt
                          ).toLocaleString()}
                        </li>
                      )}

                      {/* {data.value?.pageToken && (
                  <div className="page-token-info">
                    <small>
                      <strong>Page Token:</strong>{" "}
                      {data.value.pageToken.substring(0, 20)}...
                    </small>
                  </div>
                )} */}
                    </ul>
                  </div>
                )}

                {/* Page Token Info (for debugging) */}
                {/* {data.value?.pageToken && (
                  <div className="page-token-info">
                    <small>
                      <strong>Page Token:</strong>{" "}
                      {data.value.pageToken.substring(0, 20)}...
                    </small>
                  </div>
                )} */}

                {/* Event Types Info */}
                {/* <div className="events-info">
                  <h5>📋 Detected Events:</h5>
                  <ul className="event-types">
                    <li>🆕 New files created</li>
                    <li>✏️ Files updated</li>
                    <li>📝 Files renamed</li>
                    <li>📂 Files moved</li>
                    <li>🗑️ Files deleted/trashed</li>
                    <li>♻️ Files restored</li>
                  </ul>
                </div> */}
              </Styled>
            )}
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}